import axios from 'axios';
import { apiBase, authToken } from '../api/apiBase';

export const CALCULATOR_SHEETS = ['USD','GBP','EUR','JPY','AUD','BLOC'] as const;
export const CALCULATOR_FAMILIES = ['policy_rate_level','policy_rate_trend','inflation_yoy',
  'gdp_growth_trend','trade_balance','capital_flow_attraction','cot_bias','bond_yield_signal',
  'retail_inverted','cot_reversal','equity_signal','volatility_signal','global_risk_signal',
  'news_sentiment_signal'] as const;
export interface RawEvidenceRecord {
  record_kind?: string; article_identity?: string; canonical_url?: string; publisher?: string;
  title?: string; language?: string; publication_at?: string | null; retrieved_at?: string;
  currency_associations?: string[]; query_ids?: string[]; source_access_state?: string; source_feed_urls?: string[];
  instrument?: string; long_percentage?: number; short_percentage?: number; denominator?: string;
  instrument_label?: string; observed_at?: string; provider_update_at?: null;
  provider_update_state?: string; reference_period?: string; observation_type?: string; collection_window_id?: string;
}
export interface CalculatorEvidence {
  field_id: string; label: string; origin: string; state: string; value: number | null;
  current_eligible: boolean; unit: string | null; frequency: string | null;
  reference_period: string | null; publication_at: string | null; publication_state: string;
  source_edition_at?: string | null; scheduled_at?: string | null; observed_at?: string | null;
  provider_update_at?: null; provider_update_state?: string | null; observation_type?: string | null;
  retrieved_at: string | null; source_url: string | null; publisher: string | null;
  series_id: unknown; history_count: number; history_origin: string; history_state: string;
  reason: string | null; score: null;
  raw_record?: RawEvidenceRecord | null; raw_records?: RawEvidenceRecord[]; record_kind?: string;
  instrument_map?: {instrument: string; role: string; field_id: string; source_url?: string}[] | null;
  source_urls?: string[]; instrument_coverage?: Record<string,string> | null;
  constituent_ids?: string[]; constituent_coverage?: Record<string,string> | null; aggregation_state?: string | null;
}
export interface CalculatorSlot {
  field_id: string; sheet: string; criterion_key: string; label: string; block: string;
  evidence: CalculatorEvidence[]; supporting_evidence: CalculatorEvidence[];
  unavailable_evidence: {identity: string; state: string; reason: string}[];
  formula_state: 'FORMULA_PASS2_PENDING' | 'FORMULA_PENDING_INPUT_UNAVAILABLE'; score: null;
  calendar_bindings: {event_uuid: string; source_label: string; role: string}[];
  calendar_evidence: CalculatorEvidence[];
  upstream_formula_states: {field_id: string; state: string}[];
  workbook_destination: {named_block_input: string; UI: string; BF_outputs: string};
  downstream: Record<string,string>; BLOC_state: string | null;
}
export interface CalculatorProjection {
  success: true; schema: 'mack_calculator_evidence_projection_v1'; slots: CalculatorSlot[];
  formula_execution: false; scores_enabled: false; source_fetches: 0; writer_entries: 0;
  T1_installation: {state: string; subject: string}; generated_at: string;
}
export interface EvidenceHistory {
  success: true; field_id: string; origin: string; total: number; next_offset: number | null;
  rows: Record<string,unknown>[];
}

export function decodeCalculatorProjection(raw: unknown): CalculatorProjection {
  const p = raw as CalculatorProjection;
  if (!p || p.success !== true || p.schema !== 'mack_calculator_evidence_projection_v1' ||
      p.formula_execution !== false || p.scores_enabled !== false || p.source_fetches !== 0 ||
      p.writer_entries !== 0 || !Array.isArray(p.slots) || p.slots.length !== 84) {
    throw new Error('Calculator evidence projection unavailable');
  }
  const expected = new Set(CALCULATOR_SHEETS.flatMap(sheet =>
    CALCULATOR_FAMILIES.map(key => `mack.criterion.${sheet}.${key}`)));
  p.slots.forEach(slot => {
    if (!expected.delete(slot.field_id) || slot.field_id !== `mack.criterion.${slot.sheet}.${slot.criterion_key}` ||
        slot.score !== null || !['FORMULA_PASS2_PENDING','FORMULA_PENDING_INPUT_UNAVAILABLE'].includes(slot.formula_state) ||
        !Array.isArray(slot.evidence) || !Array.isArray(slot.supporting_evidence) || !Array.isArray(slot.unavailable_evidence)) {
      throw new Error('Calculator criterion identity/state refused');
    }
    [...slot.evidence,...slot.supporting_evidence].forEach(e => {
      if (!e || e.score !== null || (e.value !== null && (typeof e.value !== 'number' || !Number.isFinite(e.value))) ||
          (e.current_eligible && ((e.value === null && !e.raw_record && !e.raw_records?.length) || e.state !== 'current'))) {
        throw new Error('Calculator evidence value refused');
      }
      const records = e.raw_records || (e.raw_record ? [e.raw_record] : []);
      if (!Array.isArray(records) || records.length > 500 || records.some(r =>
          !r || ['score','sentiment','tone','rating','body','content','description'].some(k => k in r) ||
          (e.record_kind === 'news_corpus' && (!r.article_identity || !r.canonical_url ||
            !r.title || !r.publication_at || !r.publisher || !r.language || !r.query_ids)))) {
        throw new Error('Raw evidence metadata refused');
      }
      if (e.record_kind === 'retail_positioning' && records.some(r =>
          !['EURUSD','GBPUSD','USDJPY','AUDUSD'].includes(r.instrument || '') ||
          typeof r.long_percentage !== 'number' || !Number.isFinite(r.long_percentage) ||
          r.long_percentage < 0 || r.long_percentage > 100 ||
          typeof r.short_percentage !== 'number' || !Number.isFinite(r.short_percentage) ||
          r.short_percentage < 0 || r.short_percentage > 100 ||
          !r.observed_at || r.observed_at !== r.retrieved_at || !Number.isFinite(Date.parse(r.observed_at)) ||
          r.provider_update_at !== null || r.publication_at !== null ||
          r.provider_update_state !== 'SOURCE_DOES_NOT_EXPOSE_UPDATE_TIMESTAMP' ||
          r.reference_period !== 'CURRENT_SNAPSHOT_AT_RETRIEVAL' ||
          r.observation_type !== 'CONTINUOUS_SOURCE_SNAPSHOT' || !r.collection_window_id)) {
        throw new Error('Continuous snapshot timing refused');
      }
    });
  });
  if (expected.size) throw new Error('Calculator criterion coverage incomplete');
  return p;
}

export async function fetchCalculatorEvidence(token?: string | null): Promise<CalculatorProjection> {
  const response = await axios.get(`${apiBase()}/mack/calculator`, {
    headers: {Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache'},
  });
  return decodeCalculatorProjection(response.data);
}

export async function fetchCalculatorHistory(field: CalculatorEvidence, token?: string | null, offset = 0): Promise<EvidenceHistory> {
  const response = await axios.get(`${apiBase()}/mack/calculator/history`, {
    params: {field_id: field.field_id, origin: field.history_origin, offset, limit: 30},
    headers: {Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache'},
  });
  const data = response.data as EvidenceHistory;
  if (!data || data.success !== true || data.field_id !== field.field_id || data.origin !== field.history_origin ||
      !Array.isArray(data.rows)) throw new Error('Evidence history unavailable');
  return data;
}
